import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Modal } from '../../primitives/Modal/Modal';
import { Kbd } from '../../primitives/Kbd/Kbd';
import { useHotkey, useHotkeys, useShortcutList, type ShortcutListGroup } from '../../services/hotkeys';
import { useDsT } from '../../services/i18n';

export interface KeyboardShortcutSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** Shown when nothing is registered. */
  emptyLabel?: string;
  /** Overrides the registered list (tests, previews). */
  groups?: ShortcutListGroup[];
  /** Group headings, for localisation. */
  groupLabels?: Partial<Record<ShortcutListGroup['group'], string>>;
  /** The word between the steps of a sequence (default `then`). */
  thenLabel?: string;
}

/** The `?` sheet: every listable shortcut from `useShortcutList()`, grouped, as Kbd caps. */
export function KeyboardShortcutSheet({ open, onOpenChange, title: titleProp, emptyLabel: emptyLabelProp, groups: groupsProp, groupLabels, thenLabel: thenLabelProp }: KeyboardShortcutSheetProps) {
  const t = useDsT();
  const title = titleProp ?? t('ds.shortcutSheet.title', 'Keyboard shortcuts');
  const emptyLabel = emptyLabelProp ?? t('ds.shortcutSheet.empty', 'No shortcuts are available on this screen.');
  const thenLabel = thenLabelProp ?? t('ds.shortcutSheet.then', 'then');
  const defaultGroupLabels: Record<ShortcutListGroup['group'], string> = {
    Global: t('ds.shortcutSheet.groupGlobal', 'Global'),
    Navigation: t('ds.shortcutSheet.groupNavigation', 'Navigation'),
    Lists: t('ds.shortcutSheet.groupLists', 'Lists'),
    Editors: t('ds.shortcutSheet.groupEditors', 'Editors'),
  };
  const registered = useShortcutList();
  const { formatSteps } = useHotkeys();
  const groups = groupsProp ?? registered;
  return (
    <Modal open={open} onClose={() => onOpenChange(false)} title={title} size="lg" scrollBody>
      {groups.length === 0 ? (
        <p className="m-0 type-body text-fg-muted">{emptyLabel}</p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2">
          {groups.map((group) => (
            <section key={group.group} aria-labelledby={`zo-shortcuts-${group.group}`} className="min-w-0">
              <h3 id={`zo-shortcuts-${group.group}`} className="m-0 mb-2 type-overline text-fg-subtle">
                {groupLabels?.[group.group] ?? defaultGroupLabels[group.group] ?? group.group}
              </h3>
              <dl className="m-0 flex flex-col gap-1.5">
                {group.shortcuts.map((shortcut) => (
                  <div key={shortcut.id} className="flex items-center justify-between gap-4">
                    <dt className="min-w-0 truncate type-body text-fg">{shortcut.label}</dt>
                    <dd className="m-0 flex shrink-0 flex-wrap items-center justify-end gap-1">
                      {shortcut.keys.map((keys, alternative) => (
                        <span key={keys} className="inline-flex items-center gap-1">
                          {alternative > 0 ? <span className="type-caption text-fg-subtle">{t('ds.shortcutSheet.or', 'or')}</span> : null}
                          {formatSteps(keys).map((step, index) => (
                            <span key={`${keys}-${index}`} className="inline-flex items-center gap-1">
                              {index > 0 ? <span className="type-caption text-fg-subtle">{thenLabel}</span> : null}
                              <Kbd size="sm">{step}</Kbd>
                            </span>
                          ))}
                        </span>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}

export interface KeyboardShortcutSheetContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const KeyboardShortcutSheetContext = createContext<KeyboardShortcutSheetContextValue | null>(null);

export interface KeyboardShortcutSheetProviderProps extends Omit<KeyboardShortcutSheetProps, 'open' | 'onOpenChange'> {
  children?: ReactNode;
  /** Registers `?` to open the sheet (default true). */
  hotkey?: boolean;
  /** Label of the `?` shortcut in the sheet itself. */
  hotkeyLabel?: string;
}

/**
 * Hosts the sheet once for the app, registers `?` to open it and exposes `useKeyboardShortcutSheet()` so
 * the help and user menus can open the same sheet.
 */
export function KeyboardShortcutSheetProvider({ children, hotkey = true, hotkeyLabel: hotkeyLabelProp, ...sheetProps }: KeyboardShortcutSheetProviderProps) {
  const t = useDsT();
  const hotkeyLabel = hotkeyLabelProp ?? t('ds.shortcutSheet.show', 'Show keyboard shortcuts');
  const [isOpen, setOpen] = useState(false);
  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  // The hotkey service matches `?` by the key the keyboard produced, so `?` covers Shift+/ on every layout.
  useHotkey('?', toggle, { id: 'shell.shortcuts', label: hotkeyLabel, group: 'Global', enabled: hotkey });
  const value = useMemo(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);
  return (
    <KeyboardShortcutSheetContext.Provider value={value}>
      {children}
      <KeyboardShortcutSheet open={isOpen} onOpenChange={setOpen} {...sheetProps} />
    </KeyboardShortcutSheetContext.Provider>
  );
}

/** Opens or closes the app's shortcut sheet; no-ops outside the provider so a menu item never throws. */
export function useKeyboardShortcutSheet(): KeyboardShortcutSheetContextValue {
  const context = useContext(KeyboardShortcutSheetContext);
  return context ?? { isOpen: false, open: () => {}, close: () => {}, toggle: () => {} };
}
